import { Show } from '@cowbell/ui';
import { faCopy } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Checkbox,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNotifications } from '../../../../../_lib/notifications/useNotifications';
import { ConfidenceIndicator } from '../../../../../components/indicators';

import type { ExtractionResultItem } from '../../../../../api/document-rag/document-rag.api.types';

import { faSwapArrows } from '@fortawesome/pro-regular-svg-icons';
import classes from '../ExtractionResultCard.module.css';

/** Get a unique string value for Select component using the array index */
function getSelectValue(index: number): string {
  return index.toString();
}

export interface ExtractedResultsCardValuesProps {
  /** Extraction result item containing value, confidence, and sources */
  value: ExtractionResultItem;
  /** Extraction result items containing multiple values */
  values?: ExtractionResultItem[];
  /** Callback when copy button is clicked */
  onCopy?: () => void;
  /** Callback when swap button is clicked - receives the currently selected extraction value(s) */
  onSwapValues?: (
    values: ExtractionResultItem | ExtractionResultItem[]
  ) => void;
  /** Callback when a value is selected (single select mode only) */
  onSelect?: (value: number) => void;
  /** Callback when multiple values are selected (multi-select mode only) */
  onMultiSelect?: (indices: number[]) => void;
  /** Whether the card is in a loading state */
  isLoading?: boolean;
  /** Whether multi-select mode is enabled */
  isMultiSelect?: boolean;
  /** Selected indices for multi-select mode */
  selectedIndices?: number[];
}

export function ExtractedResultsCardValues({
  value,
  values = [],
  onCopy,
  onSwapValues,
  onSelect,
  onMultiSelect,
  isLoading,
  isMultiSelect = false,
  selectedIndices = [],
}: ExtractedResultsCardValuesProps) {
  const notifications = useNotifications();

  const handleCopy = () => {
    let textToCopy: string;

    if (isMultiSelect && selectedIndices.length > 0) {
      textToCopy = selectedIndices
        .map((index) => {
          const selectedValue = values[index];
          return String(
            selectedValue?.formatted_value ?? selectedValue?.raw_value ?? ''
          );
        })
        .filter((text) => text.trim() !== '')
        .join(', ');
    } else {
      textToCopy = String(value.formatted_value ?? value.raw_value ?? '');
    }

    if (!textToCopy || textToCopy.trim() === '') {
      notifications.error('No value to copy');
      return;
    }

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        notifications.success('Copied to clipboard');
        onCopy?.();
      })
      .catch((error) => {
        console.error('Failed to copy text:', error);
        notifications.error('Failed to copy');
      });
  };

  const displayValue = value.formatted_value ?? value.raw_value ?? null;

  const handleSingleSelect = (event: SelectChangeEvent<string>) => {
    const nextIndex = parseInt(event.target.value, 10);

    if (!isNaN(nextIndex) && nextIndex >= 0 && nextIndex < values.length) {
      onSelect?.(nextIndex);
    }
  };

  const handleMultiSelectChange = (event: SelectChangeEvent<string[]>) => {
    const selectedIndexStrings = event.target.value as string[];
    const nextIndices = selectedIndexStrings
      .map((indexStr) => parseInt(indexStr, 10))
      .filter((index) => !isNaN(index) && index >= 0 && index < values.length);

    onMultiSelect?.(nextIndices);
  };

  const getSelectedIndexStrings = (): string[] => {
    return selectedIndices
      .filter((index) => index >= 0 && index < values.length)
      .map((index) => getSelectValue(index));
  };

  const handleSwapClick = () => {
    if (isMultiSelect && selectedIndices.length > 0) {
      // Filter out invalid indices and undefined values to prevent crashes
      const selectedValues = selectedIndices
        .filter((index) => index >= 0 && index < values.length)
        .map((index) => values[index])
        .filter(
          (item): item is ExtractionResultItem =>
            item !== undefined && item !== null
        );

      if (selectedValues.length > 0) {
        onSwapValues?.(selectedValues);
      }
    } else {
      onSwapValues?.(value);
    }
  };

  const renderMultiSelectValue = (selected: string[]) => {
    if (selected.length === 0) {
      return <Typography variant="body2">Select values...</Typography>;
    }

    const validCount = selected
      .map((indexStr) => parseInt(indexStr, 10))
      .filter(
        (index) => !isNaN(index) && index >= 0 && index < values.length
      ).length;

    return (
      <Typography variant="body2" noWrap>
        {validCount} value{validCount > 1 ? 's' : ''} selected
      </Typography>
    );
  };

  const averageConfidence =
    isMultiSelect && selectedIndices.length > 0
      ? selectedIndices.reduce((sum, index) => {
          const score = values[index]?.confidence_score ?? 0;
          return sum + score;
        }, 0) / selectedIndices.length
      : value.confidence_score;

  return (
    <>
      {/* Single value display (no dropdown) */}
      <Show when={values.length <= 1 && !isMultiSelect}>
        <Typography className={classes.extractedValue}>
          {displayValue !== null && displayValue !== undefined
            ? displayValue.toString()
            : 'No value extracted'}
        </Typography>
      </Show>

      {/* Single select dropdown */}
      <Show when={values.length > 1 && !isMultiSelect}>
        <Select
          className={classes.extractedDropdownValue}
          classes={{ select: classes.selectSelect }}
          value={getSelectValue(values.indexOf(value))}
          onChange={handleSingleSelect}
          renderValue={() => (
            <Typography
              variant="body2"
              className={classes.extractedDropdownValueText}
            >
              {value.formatted_value ?? value.raw_value}
            </Typography>
          )}
        >
          {values.map((v, i) => (
            <MenuItem key={`extraction-${i}`} value={getSelectValue(i)}>
              <Stack>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  style={{ maxWidth: '37.5rem', textWrap: 'auto' }}
                >
                  {v.formatted_value ?? v.raw_value}
                </Typography>
                <Typography variant="body2" fontSize={'0.85rem'}>
                  File: {v.filename}
                </Typography>
                <Typography variant="body2" fontSize={'0.85rem'}>
                  Page: {v.page}
                </Typography>
              </Stack>
            </MenuItem>
          ))}
        </Select>
      </Show>

      {/* Multi-select dropdown */}
      <Show when={isMultiSelect && values.length > 0}>
        <Select
          multiple
          classes={{ select: classes.selectSelect }}
          value={getSelectedIndexStrings()}
          onChange={handleMultiSelectChange}
          renderValue={renderMultiSelectValue}
          sx={{ minWidth: 150, maxWidth: 300 }}
        >
          {values.map((v, i) => {
            const isSelected = selectedIndices.includes(i);
            return (
              <MenuItem key={`extraction-multi-${i}`} value={getSelectValue(i)}>
                <Checkbox checked={isSelected} size="small" />
                <ListItemText
                  primary={v.formatted_value ?? v.raw_value}
                  secondary={
                    <Stack component="span" spacing={0}>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        File: {v.filename}
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        Page: {v.page}
                      </Typography>
                    </Stack>
                  }
                />
              </MenuItem>
            );
          })}
        </Select>
      </Show>

      <ConfidenceIndicator
        value={
          averageConfidence !== undefined &&
          averageConfidence !== null &&
          !isNaN(Number(averageConfidence))
            ? averageConfidence
            : 0
        }
      />

      <Box display="flex" gap={0.5}>
        {onCopy && (
          <Tooltip title="Copy value" enterDelay={300}>
            <IconButton
              size="small"
              onClick={handleCopy}
              aria-label="Copy extracted value"
              className={classes.actionButton}
            >
              <FontAwesomeIcon size="sm" icon={faCopy} />
            </IconButton>
          </Tooltip>
        )}

        {onSwapValues && (
          <Tooltip
            title={isMultiSelect ? 'Swap selected values' : 'Swap values'}
            enterDelay={300}
          >
            <IconButton
              size="small"
              onClick={handleSwapClick}
              disabled={
                isLoading || (isMultiSelect && selectedIndices.length === 0)
              }
              aria-label={
                isMultiSelect ? 'Swap selected values' : 'Swap values'
              }
              className={`${classes.actionButton} ${
                isLoading ? classes.spinning : ''
              }`}
            >
              <FontAwesomeIcon size="sm" icon={faSwapArrows} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </>
  );
}
